import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/hooks/useAuth";
import { Button, ErrorBlock } from "@/components/Ui";

export default function VerifyEmail() {
	const navigate = useNavigate();
	const { isLoaded, isSignedIn, session } = useAuth();
	const email = session?.data?.user?.email ?? "";
	const [otp, setOtp] = useState(["", "", "", "", "", ""]);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [sending, setSending] = useState(false);
	const [cooldown, setCooldown] = useState(0);
	const [otpSent, setOtpSent] = useState(false);
	const [verified, setVerified] = useState(false);
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	useEffect(() => {
		if (isLoaded && !isSignedIn) {
			navigate("/login", { replace: true });
		}
	}, [isLoaded, isSignedIn, navigate]);

	useEffect(() => {
		if (isLoaded && isSignedIn && session?.data?.user?.emailVerified) {
			navigate("/dashboard", { replace: true });
		}
	}, [isLoaded, isSignedIn, session, navigate]);

	useEffect(() => {
		if (cooldown <= 0) return;
		const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
		return () => clearTimeout(t);
	}, [cooldown]);

	useEffect(() => {
		if (otpSent) {
			inputRefs.current[0]?.focus();
		}
	}, [otpSent]);

	const handleSendOtp = async () => {
		if (!email || cooldown > 0) return;
		setSending(true);
		setError(null);
		try {
			const { error: sendError } = await authClient.emailOtp.sendVerificationOtp({
				email,
				type: "email-verification",
			});
			if (sendError) {
				setError(sendError.message ?? "Gagal mengirim kode");
				return;
			}
			setOtpSent(true);
			setCooldown(60);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Terjadi kesalahan");
		} finally {
			setSending(false);
		}
	};

	const handleChange = (index: number, value: string) => {
		if (!/^\d*$/.test(value)) return;
		const next = [...otp];
		next[index] = value.slice(-1);
		setOtp(next);
		setError(null);
		if (value && index < 5) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
		if (e.key === "Backspace" && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e: React.ClipboardEvent) => {
		e.preventDefault();
		const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
		if (!text) return;
		const next = [...otp];
		for (let i = 0; i < 6; i++) next[i] = text[i] ?? "";
		setOtp(next);
		setError(null);
		const focusIdx = text.length < 6 ? text.length : 5;
		inputRefs.current[focusIdx]?.focus();
	};

	const code = otp.join("");

	const handleVerify = async (e: React.FormEvent) => {
		e.preventDefault();
		if (code.length !== 6) {
			setError("Masukkan 6 digit kode");
			return;
		}
		setError(null);
		setSubmitting(true);
		try {
			const { error: verifyError } = await authClient.emailOtp.verifyEmail({
				email,
				otp: code,
			});
			if (verifyError) {
				setError(verifyError.message ?? "Kode tidak valid");
				return;
			}
			setVerified(true);
			setTimeout(async () => {
				await authClient.getSession();
				navigate("/dashboard", { replace: true });
			}, 1500);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Terjadi kesalahan");
		} finally {
			setSubmitting(false);
		}
	};

	if (!isLoaded || !isSignedIn) return null;

	return (
		<div
			style={{
				flex: 1,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<div style={{ width: "100%", maxWidth: 360 }}>
				<h1
					style={{
						fontSize: "1.5rem",
						fontWeight: 800,
						margin: "0 0 0.35rem",
					}}
				>
					{verified ? "Terverifikasi!" : "Verifikasi Email"}
				</h1>
				<p
					style={{
						fontSize: "0.85rem",
						color: "var(--color-muted)",
						margin: "0 0 1.75rem",
						lineHeight: 1.5,
					}}
				>
					{verified
						? "Email kamu berhasil diverifikasi. Mengalihkan..."
						: "Email kamu belum terverifikasi. Masukkan kode verifikasi untuk melanjutkan."}
				</p>

				{!verified && !otpSent && (
					<div>
						<div
							style={{
								padding: "0.75rem 1rem",
								borderRadius: "var(--radius-md)",
								border: "1px solid var(--color-border)",
								background: "var(--color-surface)",
								marginBottom: "1.25rem",
							}}
						>
							<div
								style={{
									fontSize: "0.7rem",
									fontWeight: 600,
									color: "var(--color-muted)",
									marginBottom: "0.25rem",
								}}
							>
								Email
							</div>
							<div
								style={{
									fontSize: "0.88rem",
									fontWeight: 500,
									wordBreak: "break-all",
								}}
							>
								{email}
							</div>
						</div>

						{error && (
							<div style={{ marginBottom: "1rem" }}>
								<ErrorBlock message={error} />
							</div>
						)}

						<Button
							onClick={handleSendOtp}
							disabled={sending || cooldown > 0}
							variant="primary"
							style={{ width: "100%" }}
						>
							{sending
								? "Mengirim..."
								: cooldown > 0
									? `Kirim ulang dalam ${cooldown}s`
									: "Kirim Kode Verifikasi"}
						</Button>
					</div>
				)}

				{!verified && otpSent && (
					<>
						<div
							style={{
								padding: "0.75rem 1rem",
								borderRadius: "var(--radius-md)",
								border: "1px solid var(--color-border)",
								background: "var(--color-surface)",
								marginBottom: "1.25rem",
							}}
						>
							<div
								style={{
									fontSize: "0.7rem",
									fontWeight: 600,
									color: "var(--color-muted)",
									marginBottom: "0.25rem",
								}}
							>
								Email
							</div>
							<div
								style={{
									fontSize: "0.88rem",
									fontWeight: 500,
									wordBreak: "break-all",
								}}
							>
								{email}
							</div>
						</div>

						<p
							style={{
								fontSize: "0.82rem",
								color: "var(--color-muted)",
								margin: "0 0 1rem",
							}}
						>
							Kode 6 digit telah dikirim ke email kamu.
						</p>

						<form onSubmit={handleVerify}>
							<div
								style={{
									display: "flex",
									gap: "0.5rem",
									justifyContent: "center",
									marginBottom: "1.25rem",
								}}
							>
								{otp.map((digit, i) => (
									<input
										key={i}
										ref={(el) => { inputRefs.current[i] = el; }}
										type="text"
										inputMode="numeric"
										maxLength={1}
										value={digit}
										onChange={(e) => handleChange(i, e.target.value)}
										onKeyDown={(e) => handleKeyDown(i, e)}
										onPaste={i === 0 ? handlePaste : undefined}
										style={{
											width: "48px",
											height: "56px",
											textAlign: "center",
											fontSize: "1.4rem",
											fontWeight: 700,
											borderRadius: "var(--radius-md)",
											border: "1.5px solid var(--color-border)",
											background: "var(--color-surface)",
											color: "var(--color-text)",
											outline: "none",
											transition: "border-color 200ms ease",
										}}
										onFocus={(e) =>
											(e.currentTarget.style.borderColor = "var(--color-primary)")
										}
										onBlur={(e) =>
											(e.currentTarget.style.borderColor = "var(--color-border)")
										}
									/>
								))}
							</div>

							{error && (
								<div style={{ marginBottom: "1rem" }}>
									<ErrorBlock message={error} />
								</div>
							)}

							<Button
								type="submit"
								disabled={submitting || code.length !== 6}
								variant="primary"
								style={{ width: "100%" }}
							>
								{submitting ? "..." : "Verifikasi"}
							</Button>
						</form>

						<p
							style={{
								fontSize: "0.82rem",
								marginTop: "1.25rem",
								textAlign: "center",
								color: "var(--color-muted)",
							}}
						>
							Tidak menerima kode?{" "}
							<button
								type="button"
								onClick={handleSendOtp}
								disabled={cooldown > 0 || sending}
								style={{
									background: "none",
									border: "none",
									color:
										cooldown > 0
											? "var(--color-muted)"
											: "var(--color-primary)",
									cursor: cooldown > 0 ? "default" : "pointer",
									fontSize: "0.82rem",
									fontWeight: 600,
									padding: 0,
								}}
							>
								{cooldown > 0
									? `Kirim ulang dalam ${cooldown}s`
									: sending
										? "Mengirim..."
										: "Kirim ulang"}
							</button>
						</p>
					</>
				)}
			</div>
		</div>
	);
}
